/**
 * Chart Sizing Utilities
 * Ensures consistent, optimal chart sizing for better readability and space utilization
 */

export interface ChartDimensions {
  width: number;
  height: number;
  aspectRatio: number;
}

export interface ChartSizingOptions {
  context?: 'default' | 'blog' | 'email' | 'social-media' | 'presentation';
  chartType?: 'bar' | 'line' | 'pie' | 'horizontal-bar' | 'vertical-bar';
  dataPoints?: number;
  maxWidth?: number;
  maxHeight?: number;
}

export class ChartSizingUtils {
  // Optimal dimensions for different contexts - Increased for better visibility
  private static readonly CONTEXT_DIMENSIONS = {
    'default': { width: 1000, height: 750, aspectRatio: 4/3 },
    'blog': { width: 1200, height: 800, aspectRatio: 3/2 }, // Reduced to fit within container
    'email': { width: 1600, height: 900, aspectRatio: 16/9 }, // Much larger for email visibility
    'social-media': { width: 1800, height: 900, aspectRatio: 2 }, // Larger for social media impact
    'presentation': { width: 1800, height: 1000, aspectRatio: 9/5 }
  };

  // Minimum dimensions to ensure readability - Increased for better visibility
  private static readonly MIN_DIMENSIONS = {
    width: 800,
    height: 600
  };

  // Maximum dimensions to prevent performance issues - Increased for high-quality exports
  private static readonly MAX_DIMENSIONS = {
    width: 3000,
    height: 2000
  };

  /**
   * Get optimal chart dimensions based on context and chart type
   */
  static getOptimalDimensions(options: ChartSizingOptions = {}): ChartDimensions {
    const {
      context = 'default',
      chartType = 'bar',
      dataPoints = 10,
      maxWidth,
      maxHeight
    } = options;

    // Get base dimensions for context
    const baseDimensions = this.CONTEXT_DIMENSIONS[context];
    
    // Adjust for chart type
    let width = baseDimensions.width;
    let height = baseDimensions.height;

    switch (chartType) {
      case 'horizontal-bar':
        // Horizontal bars need more width for readability
        width = Math.max(width, dataPoints * 80);
        height = Math.max(height, 400);
        break;
      case 'vertical-bar':
        // Vertical bars can be taller for more data points
        height = Math.max(height, dataPoints * 40);
        break;
      case 'line':
        // Line charts work well with standard dimensions
        break;
      case 'pie':
        // Pie charts should be more square
        const size = Math.min(width, height);
        width = size;
        height = size;
        break;
    }

    // Apply constraints
    width = Math.max(width, this.MIN_DIMENSIONS.width);
    height = Math.max(height, this.MIN_DIMENSIONS.height);
    
    if (maxWidth) width = Math.min(width, maxWidth);
    if (maxHeight) height = Math.min(height, maxHeight);
    
    width = Math.min(width, this.MAX_DIMENSIONS.width);
    height = Math.min(height, this.MAX_DIMENSIONS.height);

    return {
      width,
      height,
      aspectRatio: width / height
    };
  }

  /**
   * Get ECharts initialization options with optimal sizing
   */
  static getEChartsInitOptions(options: ChartSizingOptions = {}): any {
    // Don't set explicit width/height - let ECharts auto-size based on container
    return {
      renderer: 'canvas',
      devicePixelRatio: window.devicePixelRatio || 1
    };
  }

  /**
   * Get container styles for optimal chart display
   */
  static getContainerStyles(options: ChartSizingOptions = {}): React.CSSProperties {
    const dimensions = this.getOptimalDimensions(options);
    
    return {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`,
      minWidth: `${this.MIN_DIMENSIONS.width}px`,
      minHeight: `${this.MIN_DIMENSIONS.height}px`,
      maxWidth: `${this.MAX_DIMENSIONS.width}px`,
      maxHeight: `${this.MAX_DIMENSIONS.height}px`
    };
  }

  /**
   * Calculate responsive dimensions based on container size
   */
  static getResponsiveDimensions(
    containerWidth: number,
    containerHeight: number,
    options: ChartSizingOptions = {}
  ): ChartDimensions {
    const optimal = this.getOptimalDimensions(options);
    
    // Calculate scale factor based on container size
    const scaleX = containerWidth / optimal.width;
    const scaleY = containerHeight / optimal.height;
    const scale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond optimal
    
    return {
      width: Math.round(optimal.width * scale),
      height: Math.round(optimal.height * scale),
      aspectRatio: optimal.aspectRatio
    };
  }

  /**
   * Get chart configuration with optimal sizing
   */
  static getSizedChartConfig(
    baseConfig: any,
    options: ChartSizingOptions = {}
  ): any {
    // Don't modify the base config - let the original grid settings work
    // The sizing is handled by the container and ECharts initialization
    return baseConfig;
  }

  /**
   * Get export dimensions for high-quality image generation
   */
  static getExportDimensions(options: ChartSizingOptions = {}): ChartDimensions {
    const base = this.getOptimalDimensions(options);
    
    // For exports, use much higher resolution for crisp social media and email images
    const multiplier = options.context === 'social-media' ? 2.5 : 2; // Extra high DPI for social media
    
    return {
      width: Math.round(base.width * multiplier),
      height: Math.round(base.height * multiplier),
      aspectRatio: base.aspectRatio
    };
  }

  /**
   * Validate chart dimensions
   */
  static validateDimensions(dimensions: ChartDimensions): boolean {
    return (
      dimensions.width >= this.MIN_DIMENSIONS.width &&
      dimensions.height >= this.MIN_DIMENSIONS.height &&
      dimensions.width <= this.MAX_DIMENSIONS.width &&
      dimensions.height <= this.MAX_DIMENSIONS.height &&
      dimensions.aspectRatio > 0
    );
  }

  /**
   * Get sizing recommendations for different use cases
   */
  static getSizingRecommendations(): Record<string, ChartSizingOptions> {
    return {
      'blog-post': {
        context: 'blog',
        maxWidth: 1000,
        maxHeight: 600
      },
      'email-newsletter': {
        context: 'email',
        maxWidth: 1200,
        maxHeight: 700
      },
      'social-media-post': {
        context: 'social-media',
        maxWidth: 1200,
        maxHeight: 630
      },
      'presentation-slide': {
        context: 'presentation',
        maxWidth: 1400,
        maxHeight: 800
      },
      'dashboard-widget': {
        context: 'default',
        maxWidth: 800,
        maxHeight: 600
      }
    };
  }
}
